import { useCallback, useEffect, useRef, useState } from 'react';
import { recordClientFailure } from '../../telemetry/rum.ts';

/* Minimal typings for the Web Speech API. The recognition half isn't in the
   standard lib.dom (and Chromium still exposes it under the `webkit` prefix),
   so we declare just the members we touch. */
interface SpeechRecognitionAlternative {
  readonly transcript: string;
}
interface SpeechRecognitionResult {
  readonly length: number;
  readonly isFinal: boolean;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEvent extends Event {
  readonly results: SpeechRecognitionResultList;
}
interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
}
interface SpeechRecognitionInstance extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  }
}

function getCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

/* Support = the Web Speech API is present. Chromium (Chrome/Edge/Brave/Opera)
   ships a reliable engine; Safari (macOS/iOS) exposes the same webkit-prefixed
   API backed by Apple's dictation engine — enabled here per user request. Safari
   can be less consistent than Chromium (permission quirks, occasional dropped
   utterances), but those surface as `onerror` events that degrade gracefully
   (`denied` hides the control) rather than breaking it. Firefox ships no
   recognition engine, so feature detection filters it out on its own. */

export interface SpeechInput {
  /** True where the Web Speech API is present (Chromium + Safari; not Firefox). */
  supported: boolean;
  /** A recognition session is active and emitting transcripts. */
  listening: boolean;
  /** Mic permission was denied this session — callers should hide the control. */
  denied: boolean;
  start: () => void;
  stop: () => void;
}

export interface SpeechInputOptions {
  /**
   * Fired when the session ends because the speaker went quiet for the full
   * {@link PAUSE_GRACE_MS} grace window, carrying the final transcript, so a
   * spoken message sends itself without a separate tap. NOT fired when the
   * caller stops the session manually via {@link SpeechInput.stop} — a manual
   * stop is "cancel".
   */
  onAutoSubmit?: (sessionTranscript: string) => void;
}

/**
 * How long the speaker can stay silent AFTER the engine ends an utterance
 * before the session finalizes and auto-submits. The engine's own endpointing
 * is aggressive (~1 s), which cut people off mid-thought; this grace window
 * lets a natural pause continue the same message. Total silence before
 * auto-submit ≈ engine endpoint (~1 s) + this.
 */
export const PAUSE_GRACE_MS = 2000;

/**
 * Hard ceiling on one listening session, regardless of what the engine does.
 *
 * The pause grace timer above is the NORMAL way a session ends, and it is
 * re-armed every time the engine finishes an utterance. That is fine as long
 * as the engine eventually stops producing utterances — but nothing in this
 * hook guaranteed it. A user reported the mic on an iPad never turning off on
 * its own, and a session that keeps hearing "speech" (background noise
 * transcribed as words, an engine that restarts and re-emits) has no other
 * exit: it chains runs indefinitely with the mic live.
 *
 * This is the backstop that makes "it never turned off" impossible. It is
 * deliberately generous — far longer than anyone dictates a chat message — so
 * it never truncates real speech; it exists only to bound a stuck session.
 */
export const MAX_SESSION_MS = 60_000;

/**
 * Client-side voice input over the Web Speech API. Each session reports the
 * running transcript (final words plus the current interim guess) via `onResult`
 * so the caller can splice it onto whatever the user had already typed. No audio
 * leaves the browser through this hook; transcription is the browser's own.
 *
 * Each engine run is single-utterance (`continuous = false`): one phrase, one
 * result updating in place. (Continuous mode was tried to tolerate longer
 * mid-sentence pauses, but this Chromium engine re-emits each interim snapshot
 * as a separate result entry in continuous mode, which the transcript builder
 * concatenated into duplicated, jumbled text — "what'swhat's thewhat's the
 * weather…". Single-utterance mode keeps one result that updates in place, so
 * the transcript is always clean.)
 *
 * To tolerate natural pauses WITHOUT continuous mode, one listening session
 * CHAINS engine runs: when the engine ends an utterance on its own, the text
 * so far is folded into a session accumulator and the engine restarts
 * immediately, still listening. If the speaker resumes, the next utterance
 * appends; if they stay quiet for {@link PAUSE_GRACE_MS}, the session
 * finalizes and `onAutoSubmit` fires with everything said. A session where
 * nothing was ever said just ends quietly on the engine's no-speech end.
 */
export function useSpeechInput(
  onResult: (sessionTranscript: string) => void,
  options: SpeechInputOptions = {},
): SpeechInput {
  const [supported] = useState(() => Boolean(getCtor()));
  const [listening, setListening] = useState(false);
  const [denied, setDenied] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  // Finalized text from earlier engine runs in this session ('' at start).
  const baseRef = useRef('');
  // The current engine run's in-place transcript.
  const utteranceRef = useRef('');
  // Pending finalize timer, armed when the engine ends and the speaker may
  // still resume; cleared if they do.
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Session is over (finalized or cancelled) — late engine events are ignored.
  const doneRef = useRef(true);
  // Absolute session deadline, armed once at start() and never extended.
  const capTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the latest callbacks without re-creating `start`, so the recognition
  // instance always reports through the current closures.
  const onResultRef = useRef(onResult);
  const onAutoSubmitRef = useRef(options.onAutoSubmit);
  useEffect(() => {
    onResultRef.current = onResult;
    onAutoSubmitRef.current = options.onAutoSubmit;
  });

  const clearGraceTimer = useCallback(() => {
    if (graceTimerRef.current !== null) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
  }, []);

  const sessionTranscript = useCallback(() => {
    const base = baseRef.current;
    const utterance = utteranceRef.current;
    if (base && utterance) {
      return `${base} ${utterance}`;
    }
    return base || utterance;
  }, []);

  /** End the session for good. Submits the transcript unless cancelled. */
  const finalize = useCallback(
    (cancelled: boolean) => {
      if (doneRef.current) {
        return;
      }
      doneRef.current = true;
      clearGraceTimer();
      if (capTimerRef.current !== null) {
        clearTimeout(capTimerRef.current);
        capTimerRef.current = null;
      }
      recognitionRef.current?.abort();
      recognitionRef.current = null;
      setListening(false);
      const finished = sessionTranscript();
      if (!cancelled && finished.trim()) {
        onAutoSubmitRef.current?.(finished);
      }
    },
    [clearGraceTimer, sessionTranscript],
  );

  const finalizeRef = useRef(finalize);
  useEffect(() => {
    finalizeRef.current = finalize;
  });

  /** Arm the pause deadline: unless the speaker resumes first, the session
   *  finalizes and auto-submits when it fires. */
  const armGraceTimer = useCallback(() => {
    if (graceTimerRef.current !== null) {
      return;
    }
    graceTimerRef.current = setTimeout(() => {
      graceTimerRef.current = null;
      finalizeRef.current(false);
    }, PAUSE_GRACE_MS);
  }, []);

  /**
   * One engine run; a listening session chains these across pauses.
   *
   * Returns whether the engine actually started. `start()` THROWS on iOS
   * Safari when a run is not tied to a user gesture — and the chained restart
   * out of `onend` never is. That throw used to escape the handler, leaving
   * `listening` true with no engine running and no way back but a manual tap:
   * the reported "the mic never turned off on its own".
   */
  const spinUp = useCallback((): boolean => {
    const Ctor = getCtor();
    if (!Ctor) {
      return false;
    }
    const recognition = new Ctor();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    // Single-utterance mode: one result updates in place as the speaker
    // talks. Avoids the continuous-mode duplication bug.
    recognition.continuous = false;
    recognition.onresult = (event) => {
      if (doneRef.current) {
        return;
      }
      let transcript = '';
      for (let i = 0; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript;
      }
      utteranceRef.current = transcript;
      // The speaker resumed inside the grace window — the session continues.
      clearGraceTimer();
      onResultRef.current(sessionTranscript());
    };
    recognition.onerror = (event) => {
      // A denied/blocked mic means the control is useless for the rest of
      // the session; flag it so the caller can hide the button permanently.
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setDenied(true);
        finalizeRef.current(true);
      }
    };
    recognition.onend = () => {
      if (doneRef.current) {
        return; // finalize/cancel already tore the session down
      }
      // Natural utterance end: fold this run's text into the session, then
      // keep listening through the pause. If nothing has been said at all,
      // end quietly (the engine's no-speech end) instead of restarting
      // forever at a silent microphone.
      baseRef.current = sessionTranscript();
      utteranceRef.current = '';
      if (!baseRef.current.trim()) {
        finalizeRef.current(true);
        return;
      }
      armGraceTimer();
      if (!spinUpRef.current()) {
        // Cannot keep listening. End the session rather than sit "listening"
        // with a dead engine, and submit what was already heard — the words
        // are real even though the chain stopped.
        finalizeRef.current(false);
      }
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      return true;
    } catch (err) {
      // Not recoverable within this session: the engine will refuse every
      // subsequent gesture-less start too. Report it — a mic that dies only on
      // one platform is invisible to us otherwise (PRINCIPLES §12).
      recognitionRef.current = null;
      recordClientFailure(
        'speech_recognition_start_failed',
        err instanceof Error ? `${err.name}: ${err.message}` : String(err),
      );
      return false;
    }
  }, [clearGraceTimer, sessionTranscript, armGraceTimer]);

  // Self-reference for the chained restart without a use-before-declare cycle.
  const spinUpRef = useRef(spinUp);
  useEffect(() => {
    spinUpRef.current = spinUp;
  });

  const start = useCallback(() => {
    if (!getCtor() || denied || recognitionRef.current) {
      return;
    }
    baseRef.current = '';
    utteranceRef.current = '';
    doneRef.current = false;
    // Armed ONCE per session and never extended — that is the whole point.
    // Anything that could push it back would reintroduce the unbounded session.
    capTimerRef.current = setTimeout(() => {
      capTimerRef.current = null;
      finalizeRef.current(false);
    }, MAX_SESSION_MS);
    if (!spinUp()) {
      // Never got off the ground — do not show a listening state.
      doneRef.current = true;
      clearTimeout(capTimerRef.current);
      capTimerRef.current = null;
      return;
    }
    setListening(true);
  }, [denied, spinUp]);

  // A manual stop is a deliberate "I'm done / cancel" — never auto-submits.
  const stop = useCallback(() => {
    finalizeRef.current(true);
  }, []);

  // Tear down a live session if the component unmounts mid-listen.
  useEffect(
    () => () => {
      finalizeRef.current(true);
    },
    [],
  );

  return { supported, listening, denied, start, stop };
}
